import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ToastService from '~/services/toast.service';

import { captureScreenshot } from './captureScreenshot';

type P = {
  readonly setScreenshot: (file: File | undefined) => void;
};

/**
 * Manual ("recapture") screenshot flow inside the bug-report dialog.
 *
 * Owns the in-progress flag the dialog uses to disable submit/minimize while
 * a capture is running, and surfaces a toast on failure — unlike the silent
 * auto-capture in `BugReportButton.handleTriggerClick`, this is user-initiated
 * so a visible error is the right UX.
 */
export function useScreenshotCapture({ setScreenshot }: P) {
  const { t } = useTranslation('bugReport');

  const [isCapturing, setIsCapturing] = useState(false);

  const handleCaptureScreenshot = async () => {
    setIsCapturing(true);

    try {
      const file = await captureScreenshot();

      setScreenshot(file);
    } catch {
      ToastService.error([t(($) => $.dialog.screenshot.errorToast)]);
    } finally {
      setIsCapturing(false);
    }
  };

  return {
    handleCaptureScreenshot,
    isCapturing,
  };
}
